import { useEffect } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import BreadcrumbStructuredData from "@/components/BreadcrumbStructuredData";
import PageSpecificFAQStructuredData from "@/components/PageSpecificFAQStructuredData";
import Breadcrumb from "@/components/Breadcrumb";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useTranslation } from "react-i18next";
import { Mail, Phone, MapPin, Clock } from "lucide-react";

const Contact = () => {
  const { t } = useTranslation(['contact', 'common']);
  
  // Load Fillout embed script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://server.fillout.com/embed/v1/';
    script.async = true;
    document.body.appendChild(script);
    
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead page="contact" />
      <BreadcrumbStructuredData />
      <Header />
      
      {/* Breadcrumb Navigation */}
      <div className="container mx-auto px-4 py-4">
        <Breadcrumb />
      </div>
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-hero">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-primary-foreground mb-6">
              {t('contact:hero.title')}
            </h1>
            <p className="text-xl text-primary-foreground/90 max-w-2xl mx-auto">
              {t('contact:hero.description')}
            </p>
          </div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-[2fr_1fr] gap-12 max-w-6xl mx-auto">
            
            {/* Contact Form */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-foreground mb-4">{t('contact:form.title')}</h2>
                <p className="text-muted-foreground">
                  Aarhus Denmark
                </p>
              </div>

              <Card>
                <CardContent className="p-8">
                  <div 
                    style={{width:'100%', height:'500px'}} 
                    data-fillout-id="1wXxxtunRSus" 
                    data-fillout-embed-type="standard" 
                    data-fillout-inherit-parameters 
                    data-fillout-dynamic-resize 
                    data-fillout-domain="form.danmag.com"
                  ></div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-foreground mb-4">{t('contact:contact.title')}</h2>
                <p className="text-muted-foreground">
                  {t('contact:contact.response')}
                </p>
              </div>

              <div className="space-y-6">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="bg-primary/10 p-3 rounded-lg">
                        <Mail className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-1">{t('common:contact.email')}</h3>
                        <a href={`mailto:${t('contact:contact.info.email')}`} className="text-primary hover:underline">
                          {t('contact:contact.info.email')}
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="bg-primary/10 p-3 rounded-lg">
                        <Phone className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-1">{t('common:contact.phone')}</h3>
                        <a href={`tel:${t('contact:contact.info.phone')}`} className="text-primary hover:underline">
                          {t('contact:contact.info.phone')}
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="bg-primary/10 p-3 rounded-lg">
                        <MapPin className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-1">{t('common:contact.location')}</h3>
                        <p className="text-muted-foreground">{t('common:contact.address')}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="bg-primary/10 p-3 rounded-lg">
                        <Clock className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-1">{t('common:contact.hours')}</h3>
                        <div className="text-muted-foreground space-y-1">
                          <p>{t('common:contact.workingHours')}</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="bg-accent/10 border border-accent/20 rounded-lg p-6">
                <h3 className="font-semibold text-foreground mb-2">{t('contact:contact.title')}</h3>
                <p className="text-muted-foreground">
                  {t('contact:contact.response')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4">{t('contact:faq.title')}</h2>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {(t('contact:faq.questions', { returnObjects: true }) as Array<{question: string, answer: string}>).map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border border-border rounded-lg px-6">
                  <AccordionTrigger className="text-left hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <PageSpecificFAQStructuredData namespace="contact" faqKey="faq.questions" />
      <Footer />
    </div>
  );
};

export default Contact;